import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { DashboardService } from '../../../../core/services/dashboard.service';
import { PeruDatePipe } from '../../../../shared/pipes/peru-date.pipe';
import { parseMoananiDateTime } from '../../../../core/utils/peru-datetime';
import { exportJsonToExcel } from '../../../../core/utils/excel-export';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    PeruDatePipe,
    RouterModule,
    CardModule,
    TableModule,
    ButtonModule,
    ChartModule,
    TagModule,
    TooltipModule,
    SelectButtonModule, FormsModule, DialogModule, DatePickerModule
  ],
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.css']
})
export class Dashboard implements OnInit {

 stats: any = null;
  loading: boolean = true;
  chartData: any;
  chartOptions: any;

  visibleStock: boolean = false;
  showPendingInMain: boolean = false;

  private pendingOrdersSnapshot: any[] = [];
  private suppressPendingOnSort = false;

  pendingOrdersListSorted: any[] = [];
  sortFieldPending: string = 'id';
  sortOrderPending: number = -1;

  rangeOptions: any[] = [
    { label: '7 Días', value: '7d' },
    { label: '15 Días', value: '15d' },
    { label: 'Mes', value: 'month' }
  ];
  selectedRange: string = '7d';
  selectedDate: Date = new Date();

  constructor(
    private dashboardService: DashboardService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.initChartOptions();
    this.loadStats();
  }

  getDateStr(): string {
    const d = this.selectedDate;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  onKpiIngresosClick() {
    this.showPendingInMain = false;
    this.cdr.detectChanges();
  }

  togglePendingInMain() {
    if (this.showPendingInMain) {
      this.showPendingInMain = false;
      this.cdr.detectChanges();
      return;
    }
    const source =
      this.pendingOrdersSnapshot.length > 0
        ? this.pendingOrdersSnapshot
        : (this.stats?.pendingOrdersList as any[]) || [];
    this.rebuildPendingTableFromSource(source);
    this.showPendingInMain = true;
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    });
  }

  private rebuildPendingTableFromSource(source: any[]) {
    const cloned = (source || []).map((o) => ({ ...o }));
    this.buildPendingOrdersSorted(cloned);
  }

  loadStats() {
    this.loading = true;
    const dateParam = this.getDateStr();
    this.dashboardService.getStats(this.selectedRange, dateParam).subscribe({
      next: (data) => {
        this.stats = data;
        this.initChartData(data.salesChart);
        const raw = Array.isArray(data.pendingOrdersList) ? data.pendingOrdersList : [];
        this.pendingOrdersSnapshot = raw.map((o: any) => ({ ...o }));
        this.buildPendingOrdersSorted(this.pendingOrdersSnapshot.map((o) => ({ ...o })));
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (e) => {
        console.error('Error cargando dashboard:', e);
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onRangeChange() {
    this.loadStats();
  }

  onDateChange() {
    this.loadStats();
  }

  exportExcel() {
    if (!this.stats || !this.stats.salesChart) return;

    const values = this.stats.salesChart.values as number[];
    const total = values.reduce((acc, v) => acc + (v || 0), 0);

    const exportData = this.stats.salesChart.labels.map((label: string, index: number) => ({
      Fecha: label,
      Venta: this.stats.salesChart.values[index]
    }));
    exportData.push({ Fecha: 'TOTAL', Venta: total });

    void exportJsonToExcel(exportData, `reporte_ventas_export_${new Date().getTime()}.xlsx`);
  }

  exportPdf() {
    if (!this.stats || !this.stats.salesChart) return;

    const doc = new jsPDF();
    const values = this.stats.salesChart.values as number[];
    const total = values.reduce((acc, v) => acc + (v || 0), 0);

    doc.setFontSize(18);
    doc.text('Reporte de Evolución de Ventas', 14, 20);
    doc.setFontSize(12);
    doc.text(`Rango: ${this.getRangeLabel()}`, 14, 30);
    doc.text(`Fecha de emisión: ${new Date().toLocaleDateString()}`, 14, 38);

    const body = this.stats.salesChart.labels.map((label: string, index: number) => [
      label,
      `S/ ${this.stats.salesChart.values[index].toFixed(2)}`
    ]);
    body.push(['TOTAL', `S/ ${total.toFixed(2)}`]);

    autoTable(doc, {
      startY: 45,
      head: [['Fecha', 'Monto de Venta']],
      body: body,
      theme: 'grid',
      headStyles: { fillColor: [0, 0, 0] },
    });

    doc.save('reporte_ventas.pdf');
  }

  getRangeLabel(): string {
    const opt = this.rangeOptions.find(o => o.value === this.selectedRange);
    return opt ? opt.label : 'Personalizado';
  }

  initChartData(apiData: any) {
    if(!apiData) return;
    this.chartData = {
      labels: apiData.labels,
      datasets: [
        {
          label: 'Ventas (S/.)',
          data: apiData.values,
          backgroundColor: '#3b82f6',
          hoverBackgroundColor: '#2563eb',
          borderRadius: 6,
          borderSkipped: false,
          barPercentage: 0.6
        }
      ]
    };
  }

  initChartOptions() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    this.chartOptions = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: { display: false },
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                label: function(context: any) {
                    let label = context.dataset.label || '';
                    if (label) label += ': ';
                    if (context.parsed.y !== null) {
                        label += new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(context.parsed.y);
                    }
                    return label;
                }
            }
        }
      },
      scales: {
        x: {
            ticks: { color: textColorSecondary, font: { weight: 500 } },
            grid: { color: 'transparent', drawBorder: false }
        },
        y: {
            ticks: { color: textColorSecondary, callback: function(value: any) { return 'S/ ' + value; } },
            grid: { color: surfaceBorder, drawBorder: false, borderDash: [5, 5] }
        }
      }
    };
  }

  getSeverity(status: string): "success" | "info" | "warn" | "danger" | "secondary" | "contrast" | undefined {
    switch(status) {
      case 'ENTREGADO': return 'success';
      case 'CONFIRMADO': return 'info';
      case 'PAGADO': return 'success';
      case 'EN_PREPARACION': return 'warn';
      case 'PENDIENTE': return 'warn';
      case 'CANCELADO': return 'danger';
      default: return 'secondary';
    }
  }

getStockSeverity(qty: number): "success" | "info" | "warn" | "danger" | "secondary" | "contrast" | undefined {
    if(qty === 0) return 'danger';
    if(qty <= 2) return 'danger';
    if(qty <= 5) return 'warn';
    return 'success';
  }

  getOrderCategoryLabel(order: any): string {
    if (!order?.items?.length) return '—';
    const names = order.items.map((i: any) => i.product?.categoryName || i.product?.modelo?.category?.name).filter(Boolean);
    return [...new Set(names)].slice(0, 2).join(', ') || '—';
  }

  private buildPendingOrdersSorted(list: any[]) {
    this.pendingOrdersListSorted = (list || []).map(o => ({
      ...o,
      _tipoEnvio: (o.tipoEnvio?.nombreTipoEnvio || o.deliveryMethod || '').toString().toLowerCase()
    }));
    this.sortFieldPending = 'id';
    this.sortOrderPending = -1;
    this.applyPendingSort();
  }

  onPendingSort(event: { field: string; order: number }) {
    if (this.suppressPendingOnSort) {
      return;
    }
    this.sortFieldPending = event.field;
    this.sortOrderPending = event.order;
    this.applyPendingSort();
  }

  private applyPendingSort() {
    const field = this.sortFieldPending;
    const order = this.sortOrderPending;
    if (!field || !this.pendingOrdersListSorted?.length) {
      return;
    }
    const sorted = [...this.pendingOrdersListSorted].sort((a, b) => {
      let va = this.getSortValue(a, field);
      let vb = this.getSortValue(b, field);
      if (va == null && vb == null) return 0;
      if (va == null) return order === 1 ? 1 : -1;
      if (vb == null) return order === 1 ? -1 : 1;
      if (typeof va === 'number' && typeof vb === 'number') return order === 1 ? va - vb : vb - va;
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      const cmp = sa.localeCompare(sb, 'es');
      return order === 1 ? cmp : -cmp;
    });
    this.suppressPendingOnSort = true;
    this.pendingOrdersListSorted = sorted;
    queueMicrotask(() => {
      this.suppressPendingOnSort = false;
    });
  }

  private getSortValue(o: any, field: string): any {
    if (field === '_tipoEnvio') return o._tipoEnvio;
    const v = o[field];
    if (field === 'scheduledDate' && v)
      return parseMoananiDateTime(v)?.getTime() ?? 0;
    return v;
  }
}